import { forwardRef } from 'react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { useLazyOptimizelyFeatureFlag } from '@zoopla/optimizely';
import { Box } from '@zoopla-eng/zds/box';
import { fontGroups } from '@zoopla-eng/zds/themes';
import {
    styledLegendStyle,
    styledErrorStyle,
    styledRadioButtonStyle
} from './EmailPreferencesToggle.styles.css';

interface EmailPreferencesToggleProps
    extends Omit<
        RadioGroup.RadioGroupItemProps,
        'value' | 'aria-describedby' | 'id' | 'onChange'
    > {
    name: string;
    legend: string;
    label: string;
    errorText?: string;
    onChange: RadioGroup.RadioGroupProps['onValueChange'];
}

export const EmailPreferencesToggle = forwardRef<
    HTMLButtonElement,
    EmailPreferencesToggleProps
>(({ legend, label, errorText, onChange, name, ...props }, ref) => {
    const [, isFeatureEnabled] = useLazyOptimizelyFeatureFlag(
        'client-crme-1822-preferences-buttons'
    );

    return (
        <>
            <RadioGroup.Root
                aria-labelledby="email-preferences-label"
                aria-describedby="email-preferences-legend"
                required
                name={name}
                onValueChange={onChange}
            >
                <Box pb="S200">
                    <legend
                        className={`${styledLegendStyle} ${fontGroups.ladle}`}
                        id="email-preferences-legend"
                        data-testid="email-preferences-legend"
                    >
                        {legend}
                    </legend>
                </Box>
                <Box pb="M100">
                    <label
                        className={fontGroups.ladle}
                        id="email-preferences-label"
                        data-testid="email-preferences-label"
                    >
                        {label}
                    </label>
                </Box>
                <Box display="flex" columnGap="S400">
                    <RadioGroup.Item
                        asChild
                        value="yes"
                        id="yes"
                        ref={ref}
                        aria-describedby="email-preferences-legend"
                        {...props}
                    >
                        <button
                            className={`${styledRadioButtonStyle({
                                variant: isFeatureEnabled ? 'unified' : 'yes',
                                error: !!errorText
                            })} ${fontGroups.ladle}`}
                        >
                            Yes, please
                        </button>
                    </RadioGroup.Item>
                    <RadioGroup.Item
                        asChild
                        value="no"
                        id="no"
                        ref={ref}
                        aria-describedby="email-preferences-legend"
                        {...props}
                    >
                        <button
                            className={`${styledRadioButtonStyle({
                                variant: isFeatureEnabled ? 'unified' : 'no',
                                error: !!errorText
                            })} ${fontGroups.ladle}`}
                        >
                            No, thanks
                        </button>
                    </RadioGroup.Item>
                </Box>
            </RadioGroup.Root>
            <div
                aria-live="assertive"
                data-testid="email-preferences-error-message"
            >
                {errorText && (
                    <Box pt="S300">
                        <p className={`${styledErrorStyle} ${fontGroups.fork}`}>
                            {errorText}
                        </p>
                    </Box>
                )}
            </div>
        </>
    );
});
